import isEqual from 'lodash/isEqual';

import { Maybe } from 'Src/generated/graphql';

import {
  EMPTY_PRODUCT_PACKAGE,
  ProductPackage,
} from '../../../schema/ProductPackage';
import {
  DEFAULT_HIDDEN_COLUMNS,
  HIDDEN_COLUMNS_NAMESPACE,
} from '../../constants';
import { RootAction, RootActionType } from '../actions';
import { RootState } from '../reducer';
import { focusedCellReducer } from './focusedCell';

export type FilterRollups = {
  type: string;
  rollupMarkets: {
    nodes: {
      marketId: string;
      marketName?: Maybe<string>;
      currencyCode?: Maybe<string>;
      options: {
        nodes: {
          label: string;
          missing: boolean;
          ranges: {
            nodes: {
              label: string;
              value: number;
            }[];
          };
          allocated?: Maybe<{
            nodes: {
              label: string;
              value: number;
            }[];
          }>;
          unallocated?: Maybe<{
            nodes: {
              label: string;
              value: number;
            }[];
          }>;
        }[];
      };
    }[];
  };
  truckloadTotals?: Maybe<{
    totalGrossWeight?: Maybe<number>;
    totalCube?: Maybe<number>;
    totalTrucks?: Maybe<number>;
    totalQuantity?: Maybe<number>;
    totalUnits?: Maybe<number>;
    totalCases?: Maybe<number>;
    totalPackages?: Maybe<number>;
  }>;
}[];

export type LinesheetState = {
  attributes: readonly { label: string; inturnType: string }[];
  chosenRows: Set<string>;
  entity: Entity | null;
  filterKey: string | null;
  filterRollups: FilterRollups | null;
  hiddenColumns: Set<string>;
  isAllRowsSelected: boolean;
  isStale: boolean;
  rows: Row[] | null;
  savedMarketUuid: string | null;
  savedUploadChannelUuid: string | null;
  scrollToIndex: number | null;
  sortDirection: SortDirection | null;
  sortKey: SortKey | null;
  sortScope: SortScope | null;
  sortScopeValue: string | null;
  totalCasepackCasesInView: number | null;
  totalUnitsInView: number | null;
  availableQuantity: number | null;
  viewableStartIndex: number | null;
  viewableStopIndex: number | null;
};

export type Row = ProductPackage & AdditionalData;

type AdditionalData = {
  index: number;
};

export enum Entity {
  Casepack = 'casepack',
  Item = 'item',
}

export enum SortKey {
  EntityAttribute = 'EntityAttribute',
  EntityCost = 'EntityCost',
  EntityId = 'EntityId',
  EntityMsrp = 'EntityMsrp',
  EntityName = 'EntityName',
  EntityWholesale = 'EntityWholesale',
  TotalPackageUnits = 'TotalPackageUnits',
  TotalPackageUnitsAvailable = 'TotalPackageUnitsAvailable',
  TotalProductUnits = 'TotalProductUnits',
  TotalProductUnitsAvailable = 'TotalProductUnitsAvailable',
  TotalProductsPerPackage = 'TotalProductsPerPackage',
}

export enum SortDirection {
  Ascending = 'ASC',
  Descending = 'DESC',
}

export enum SortScope {
  EntityAttribute = 'EntityAttribute',
  EntityMarket = 'EntityMarket',
  QuantitySummaryGroup = 'QuantitySummaryGroup',
}

export const initialLinesheetState = {
  attributes: [],
  chosenRows: new Set([]),
  entity: getInitialEntity(),
  filterKey: null,
  filterRollups: null,
  hiddenColumns: getInitialHiddenColumns(),
  isAllRowsSelected: false,
  isStale: false,
  rows: null,
  savedMarketUuid: null,
  savedUploadChannelUuid: null,
  scrollToIndex: null,
  sortDirection: SortDirection.Ascending,
  sortKey: SortKey.EntityId,
  sortScope: null,
  sortScopeValue: null,
  totalCasepackCasesInView: null,
  totalUnitsInView: null,
  availableQuantity: null,
  viewableStartIndex: null,
  viewableStopIndex: null,
};

export function linesheetReducer(
  state: LinesheetState,
  action: RootAction,
  rootState: RootState,
): LinesheetState {
  switch (action.type) {
    case RootActionType.ClearLinesheetRows:
      if (state.rows === null) return state;

      return {
        ...state,
        rows: null,
      };

    case RootActionType.FillLinesheetWithEmptyCasepacks:
    case RootActionType.FillLinesheetWithEmptyItems:
      if (
        !state.rows ||
        state.rows.every((row) => row.id === EMPTY_PRODUCT_PACKAGE.id)
      ) {
        return state;
      }

      return {
        ...state,
        rows: Array(state.rows.length)
          .fill(EMPTY_PRODUCT_PACKAGE)
          .map((row, index) => ({
            ...row,
            index,
          })),
      };

    case RootActionType.MoveLinesheetFocusedCellBackward:
    case RootActionType.MoveLinesheetFocusedCellDown:
    case RootActionType.MoveLinesheetFocusedCellForward:
    case RootActionType.MoveLinesheetFocusedCellUp: {
      const resultFocusedCell = focusedCellReducer(
        rootState.focusedCell,
        action,
        rootState,
      );

      if (
        resultFocusedCell.row === null ||
        state.viewableStopIndex === null ||
        state.viewableStartIndex === null
      ) {
        return state;
      }

      if (resultFocusedCell.row < state.viewableStartIndex) {
        return {
          ...state,
          scrollToIndex: state.viewableStartIndex - 1,
        };
      }

      if (resultFocusedCell.row > state.viewableStopIndex) {
        return {
          ...state,
          scrollToIndex: state.viewableStartIndex + 1,
        };
      }

      return state;
    }

    case RootActionType.ReceiveLinesheetAttributes:
      if (isEqual(state.attributes, action.payload)) {
        return state;
      }

      return {
        ...state,
        attributes: action.payload,
      };

    case RootActionType.ReceiveLinesheetFilterKey:
      if (state.filterKey === action.payload) return state;

      return {
        ...state,
        filterKey: action.payload,
      };

    case RootActionType.ReceiveLinesheetFilterRollups:
      return {
        ...state,
        filterRollups: action.payload,
      };

    case RootActionType.ReceiveLinesheetRows:
      return {
        ...state,
        isStale: false,
        rows: action.payload,
      };

    case RootActionType.ReceiveMoreLinesheetRows:
      if (!state.rows) return state;

      const rows = [...state.rows];

      for (let i = 0; i < action.payload.rows.length; i += 1) {
        rows[i + action.payload.offset] = action.payload.rows[i];
      }

      return {
        ...state,
        rows,
        isStale: false,
      };

    case RootActionType.RemoveLinesheetSort:
      if (
        state.sortDirection === null &&
        state.sortKey === null &&
        state.sortScope === null &&
        state.sortScopeValue === null
      ) {
        return state;
      }

      return {
        ...state,
        sortDirection: null,
        sortKey: null,
        sortScope: null,
        sortScopeValue: null,
      };

    case RootActionType.ResetLinesheetSelections:
      if (
        state.chosenRows === initialLinesheetState.chosenRows &&
        state.isAllRowsSelected === initialLinesheetState.isAllRowsSelected
      ) {
        return state;
      }

      return {
        ...state,
        chosenRows: initialLinesheetState.chosenRows,
        isAllRowsSelected: initialLinesheetState.isAllRowsSelected,
      };

    case RootActionType.ReceiveLinesheetTotalCasepackCasesInView:
      return {
        ...state,
        totalCasepackCasesInView: action.payload,
      };

    case RootActionType.ReceiveLinesheetTotalUnitsInView:
      return {
        ...state,
        totalUnitsInView: action.payload,
      };

    case RootActionType.ReceiveLinesheetAvailableQuantity:
      return {
        ...state,
        availableQuantity: action.payload,
      };

    case RootActionType.ResetLinesheetSort:
      if (
        state.sortDirection === initialLinesheetState.sortDirection &&
        state.sortKey === initialLinesheetState.sortKey &&
        state.sortScope === initialLinesheetState.sortScope &&
        state.sortScopeValue === initialLinesheetState.sortScopeValue
      ) {
        return state;
      }

      return {
        ...state,
        sortDirection: initialLinesheetState.sortDirection,
        sortKey: initialLinesheetState.sortKey,
        sortScope: initialLinesheetState.sortScope,
        sortScopeValue: initialLinesheetState.sortScopeValue,
      };

    case RootActionType.SetIsLinesheetStale:
      if (state.isStale === action.payload) return state;

      return {
        ...state,
        isStale: action.payload,
      };

    case RootActionType.SetLinesheetEntity:
      if (state.entity === action.payload) return state;

      return {
        ...state,
        entity: action.payload,
        rows: null,
        sortDirection: initialLinesheetState.sortDirection,
        sortKey: initialLinesheetState.sortKey,
        sortScope: initialLinesheetState.sortScope,
        sortScopeValue: initialLinesheetState.sortScopeValue,
      };

    case RootActionType.SetLinesheetScrollToIndex:
      if (action.payload === state.scrollToIndex) return state;

      return {
        ...state,
        scrollToIndex: action.payload,
      };

    case RootActionType.SetLinesheetHiddenColumns:
      return {
        ...state,
        hiddenColumns: action.payload,
      };

    case RootActionType.SetLinesheetSort: {
      const hasKeyChanged = action.payload.key !== state.sortKey;
      const hasScopeChanged = action.payload.scope !== state.sortScope;
      const hasScopeValueChanged =
        action.payload.scopeValue !== state.sortScopeValue;
      const hasDirectionChanged =
        action.payload.direction !== state.sortDirection;

      const isTogglingDirectionOnly =
        !hasKeyChanged &&
        !hasScopeChanged &&
        !hasScopeValueChanged &&
        !action.payload.direction;

      if (isTogglingDirectionOnly) {
        return {
          ...state,
          isStale: true,
          sortDirection:
            state.sortDirection === SortDirection.Ascending
              ? SortDirection.Descending
              : SortDirection.Ascending,
        };
      }

      if (
        hasKeyChanged ||
        hasScopeChanged ||
        hasScopeValueChanged ||
        hasDirectionChanged
      ) {
        return {
          ...state,
          isStale: true,
          sortKey: action.payload.key,
          sortDirection: action.payload.direction || SortDirection.Ascending,
          sortScope: action.payload.scope || null,
          sortScopeValue: action.payload.scopeValue || null,
        };
      }

      return state;
    }

    case RootActionType.SetLinesheetSavedMarketUuid:
      if (state.savedMarketUuid === action.payload) return state;

      return {
        ...state,
        savedMarketUuid: action.payload,
      };

    case RootActionType.SetLinesheetSavedUploadChannelUuid:
      if (state.savedUploadChannelUuid === action.payload) return state;

      return {
        ...state,
        savedUploadChannelUuid: action.payload,
      };

    case RootActionType.SetLinesheetViewableIndexes:
      if (
        action.payload.startIndex === state.viewableStartIndex &&
        action.payload.stopIndex === state.viewableStopIndex
      ) {
        return state;
      }

      return {
        ...state,
        scrollToIndex: initialLinesheetState.scrollToIndex,
        viewableStartIndex: action.payload.startIndex,
        viewableStopIndex: action.payload.stopIndex,
      };

    case RootActionType.ToggleSelectAllLinesheetRows:
      return {
        ...state,
        chosenRows: initialLinesheetState.chosenRows,
        isAllRowsSelected: !state.isAllRowsSelected,
      };

    case RootActionType.ToggleSelectLinesheetRow:
      const chosenRows = new Set(state.chosenRows);

      if (state.chosenRows.has(action.payload)) {
        chosenRows.delete(action.payload);
      } else {
        chosenRows.add(action.payload);
      }

      return {
        ...state,
        chosenRows,
      };

    default:
      return state;
  }
}

function getInitialEntity() {
  if (window.location.pathname.match('/inventory/casepacks')) {
    return Entity.Casepack;
  }

  if (window.location.pathname.match('/inventory/items')) {
    return Entity.Item;
  }

  return null;
}

function getInitialHiddenColumns() {
  const savedHiddenColumnsJSON = window.localStorage.getItem(
    HIDDEN_COLUMNS_NAMESPACE,
  );

  let result: Set<string>;

  try {
    const hiddenColumnsArray = JSON.parse(savedHiddenColumnsJSON || '');
    result = new Set(hiddenColumnsArray);
  } catch (error) {
    result = DEFAULT_HIDDEN_COLUMNS;
  }

  return result;
}
